import { mapValues, memoize } from '@exodus/basic-utils';
import ExchangeProviders, { AssetsService, OrdersService, Providers, AssetAvailabilityService, } from '@exodus/exchange-client-providers';
import createFetchival from '@exodus/fetch/create-fetchival';
import AssetAdapter from '../adapters/asset.js';
import PairAdapter from '../adapters/pair.js';
const findAsset = memoize((assets, assetId) => {
    return Object.values(assets).find((asset) => asset.ticker === assetId);
}, (_assets, assetId) => assetId, 1000 * 60 * 5);
export class ExchangeProvidersModule {
    #exchangeProviders;
    #geolocationAtom;
    #exchangeRemoteConfigAtom;
    #assetsModule;
    #subscriptions = [];
    #appVersion;
    #assetAvailabilityService;
    constructor({ config, fetch, geolocationAtom, logger, exchangeTracer, exchangeRemoteConfigAtom, assetsModule, }) {
        const fetchival = createFetchival({ fetch });
        this.#appVersion = config.appVersion || config.providers.direct.appVersion;
        const ecpLogger = config.debug
            ? mapValues(logger, (loggerFn) => (...args) => loggerFn('exchange-client-providers', ...args))
            : undefined;
        const currencyProvider = {
            getCurrency: (assetId, _decimals) => {
                const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
                const walletId = assetAdapter.toWalletId(assetId);
                return findAsset(this.#assetsModule.getAssets(), walletId)?.currency;
            },
        };
        const assetsService = new AssetsService({
            ...config.services.assets,
            fetchival,
            currencyProvider,
        });
        const ordersService = new OrdersService({
            ...config.services.orders,
            fetchival,
        });
        this.#assetAvailabilityService = new AssetAvailabilityService({
            assetsService,
            refreshIntervalMs: config.assetAvailabilityRefreshIntervalMs,
        });
        const providers = [
            config.providers.direct.enabled &&
                new Providers.Direct({
                    ...config.providers.direct,
                    fetchival,
                    assetsService,
                    tracer: exchangeTracer,
                    assetAvailabilityService: this.#assetAvailabilityService,
                }),
            config.providers.indirect?.enabled &&
                new Providers.Indirect({
                    ...config.providers.indirect,
                    fetchival,
                    assetsService,
                    ordersService,
                    tracer: exchangeTracer,
                    assetAvailabilityService: this.#assetAvailabilityService,
                }),
            config.providers.chained?.enabled &&
                new Providers.Chained({
                    ...config.providers.chained,
                    fetchival,
                    assetsService,
                    ordersService,
                    assetAvailabilityService: this.#assetAvailabilityService,
                }),
        ].filter(Boolean);
        this.#exchangeProviders = new ExchangeProviders({
            providers,
            logger: ecpLogger,
            assetAvailabilityService: this.#assetAvailabilityService,
        });
        this.#geolocationAtom = geolocationAtom;
        this.#exchangeRemoteConfigAtom = exchangeRemoteConfigAtom;
        this.#assetsModule = assetsModule;
    }
    getAssetAvailabilityService() {
        return this.#assetAvailabilityService;
    }
    getSources() {
        return this.#exchangeProviders.getSources();
    }
    getProviderByStrategy(...args) {
        return this.#exchangeProviders.getProviderByStrategy(...args);
    }
    getQuotes(params) {
        const pairAdapter = new PairAdapter({ assetsModule: this.#assetsModule });
        const pairExchangeId = pairAdapter.toExchangePairId(params.pairId);
        return this.#exchangeProviders.getQuotes({ ...params, pairId: pairExchangeId });
    }
    exchange(params) {
        const pairAdapter = new PairAdapter({ assetsModule: this.#assetsModule });
        const pairExchangeId = pairAdapter.toExchangePairId(params.pairId);
        return this.#exchangeProviders.exchange({ ...params, pairId: pairExchangeId });
    }
    getExchangeLimits(params) {
        const pairAdapter = new PairAdapter({ assetsModule: this.#assetsModule });
        const pairExchangeId = pairAdapter.toExchangePairId(params.pairId);
        return this.#exchangeProviders.getExchangeLimits({ ...params, pairId: pairExchangeId });
    }
    load = () => {
        const geolocationSubscription = this.#geolocationAtom.observe(this.#handleGeolocationChange);
        const remoteConfigSubscription = this.#exchangeRemoteConfigAtom.observe(this.#handleRemoteConfigChange);
        this.#subscriptions.push(geolocationSubscription, remoteConfigSubscription);
    };
    stop = () => {
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
    };
    #handleGeolocationChange = (geolocationValue) => {
        this.#exchangeProviders.setGeolocation(geolocationValue);
    };
    #handleRemoteConfigChange = async (value) => {
        const defaultConfig = value.default;
        const versionConfig = value[this.#appVersion];
        const config = {
            ...defaultConfig,
            ...versionConfig,
        };
        const entries = Object.entries(config);
        entries.forEach(([providerName, { enabled, ...providerConfig }]) => {
            if (typeof enabled !== 'boolean') {
                return;
            }
            if (enabled) {
                this.#exchangeProviders.enableProvider(providerName);
            }
            else {
                this.#exchangeProviders.disableProvider(providerName);
            }
            if ('transactionPrivacyNetworks' in providerConfig) {
                const { transactionPrivacyNetworks } = providerConfig;
                if (Array.isArray(transactionPrivacyNetworks)) {
                    this.#exchangeProviders.setTransactionPrivacyNetworks(providerName, transactionPrivacyNetworks);
                }
            }
        });
    };
}
const exchangeProvidersModuleFactory = (args) => new ExchangeProvidersModule({ ...args });
const exchangeProvidersModuleDefinition = {
    id: 'exchangeProviders',
    type: 'module',
    factory: exchangeProvidersModuleFactory,
    dependencies: [
        'config',
        'logger',
        'geolocationAtom',
        'fetch',
        'exchangeTracer?',
        'exchangeRemoteConfigAtom',
        'assetsModule',
    ],
};
export default exchangeProvidersModuleDefinition;
